import { useState } from "react";
import { Radio, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useLive } from "@/contexts/LiveContext";

/**
 * GoLiveButton — single-purpose CTA used on Event Detail, Group page,
 * and on Workout event cards. Posts to /api/live/start with the given
 * (scope, target_id) then opens the LiveRoomModal for the new session.
 *
 * Members of the host group automatically see a "Live Now" banner in the
 * AlertBar within ~8s thanks to LiveContext polling.
 *
 * props:
 *   scope      — "group" | "event"
 *   targetId   — group_id or event_id
 *   label      — optional override (default: "Go Live")
 *   variant    — "primary" (red) | "ghost"
 *   testId     — base data-testid for the button
 */
export default function GoLiveButton({
  scope,
  targetId,
  label = "Go Live",
  variant = "primary",
  testId = "go-live-btn",
  title,
  style: styleOverride,
}) {
  const { openRoom, refresh } = useLive();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const start = async () => {
    if (!targetId || busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/live/start", {
        scope,
        target_id: targetId,
      });
      refresh && refresh();
      openRoom(data.session_id);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  const baseStyle = variant === "primary"
    ? {
        background: "#ff3b30",
        color: "#fff",
        borderColor: "#ff3b30",
        fontWeight: 700,
        boxShadow: "0 4px 14px rgba(255,59,48,0.35)",
      }
    : {};

  return (
    <div className="inline-flex flex-col items-stretch gap-1">
      <button
        type="button"
        onClick={start}
        disabled={busy || !targetId}
        className={variant === "primary" ? "bd-btn" : "bd-btn bd-btn-ghost"}
        style={{ ...baseStyle, ...(styleOverride || {}) }}
        data-testid={testId}
        title={title || `Go live for everyone in this ${scope}`}
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Radio size={14} />}
        {label}
      </button>
      {err && (
        <div
          className="text-[11px] px-2 py-1 rounded"
          style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }}
          data-testid={`${testId}-error`}
        >
          {err}
        </div>
      )}
    </div>
  );
}
